import type React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';

import { useContactSupport } from '../../../state';
import { createDocumentationURL } from '../../../utils';

/**
 * Empty states for integrations.
 */
export function IntegrationsEmptyState(props: {
    kind?: 'error' | 'not-found' | 'no-custom' | 'no-installations' | 'no-installation';
    className?: string;
}): React.ReactElement {
    const { kind = 'not-found', className } = props;
    const contactSupport = useContactSupport();

    const kindProps = {
        error: {
            title: `We couldn't load the integration(s).`,
            description:
                'Try reloading the page, and if the problem persists, reach out to our support team.',
            actions: (
                <EmptyStateActions>
                    <Button
                        label="Reload"
                        kind="secondary"
                        leadingIcon={Icon.Reset}
                        onPress={() => {
                            window.location.reload();
                        }}
                    />
                    <Button
                        label="Contact support"
                        kind="secondary"
                        leadingIcon={Icon.Send}
                        onPress={() => contactSupport()}
                    />
                </EmptyStateActions>
            ),
        },
        'not-found': {
            title: 'No integrations found.',
            description: 'No integrations match your search criteria.',
        },
        'no-custom': {
            title: 'No custom integrations found.',
            description:
                'Head over to our developer documentation to discover how to build your own integrations.',
            actions: (
                <EmptyStateActions>
                    <Button
                        label="Read the docs"
                        kind="secondary"
                        leadingIcon={Icon.Documentation}
                        href={createDocumentationURL('integrations/integrations', 'developer')}
                        target="_blank"
                    />
                </EmptyStateActions>
            ),
        },
        'no-installations': {
            title: 'No installed integrations found.',
            description:
                'Browse integrations then install and configure to add them to this space.',
            actions: (
                <EmptyStateActions>
                    <Button
                        label="Browse integrations"
                        kind="secondary"
                        leadingIcon={Icon.Integrations}
                        href={{
                            current: {
                                sideSheet: {
                                    type: 'integrations',
                                    view: { type: 'category', category: undefined },
                                },
                            },
                        }}
                    />
                </EmptyStateActions>
            ),
        },
        'no-installation': {
            title: "We couldn't load the integration installation",
            description:
                'Try reloading the page, and if the problem persists, reach out to our support team.',
            actions: (
                <EmptyStateActions>
                    <Button
                        label="Reload"
                        kind="secondary"
                        leadingIcon={Icon.Reset}
                        onPress={() => {
                            window.location.reload();
                        }}
                    />
                    <Button
                        label="Contact support"
                        kind="secondary"
                        leadingIcon={Icon.Send}
                        onPress={() => contactSupport()}
                    />
                </EmptyStateActions>
            ),
        },
    }[kind];

    return (
        <EmptyState className={className}>
            <EmptyStateIcon>
                <Icon.Integrations />
            </EmptyStateIcon>
            <Heading>{kindProps.title}</Heading>
            <Description>{kindProps.description}</Description>
            {kindProps.actions || null}
        </EmptyState>
    );
}
